import { FormComboBox } from '@/components/forms/form-combo-box.js';
import { FormInputDate } from '@/components/forms/form-input-date.js';
import { FormMultiSelect } from '@/components/forms/form-multi-select.js';
import { FormRadioGroup } from '@/components/forms/form-radio-group.js';
import { FormSingleSelect } from '@/components/forms/form-single-select.js';
import { FormTextArea } from '@/components/forms/form-text-area.js';
import { Label } from '@/components/ui/label.js';
import { MultiSelectOption } from '@/components/ui/multi-select.js';
import {
  buildNameLikeSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
} from '@/components/ui/multi-select-utils.js';
import {
  coachesTypeaheadSearch,
  useAllLearningPaths,
  useAvailableCoachesSearch,
  useClassTimeOptions,
  userTypeaheadSearch,
} from '@/lib/api-call-hooks.js';
import { EmptyArray, getCoach, getCoaches } from '@/lib/api-calls.js';
import { AllSelectTeacherTypes, AllUpdateTeacherTypeValues, CoachesTypeaheadSearchOptions } from '@/lib/coach-utils.js';
import { formCreateEditClassSchema, getAttendanceStatusLabel, emailsSentOptions } from '@/lib/student-group-utils.js';
import {
  Final14DayProtectionNotice,
  ProtectedPeriodOverrideFields,
} from '@/routes/_layout/student-groups/-components/protected-period-override-fields.js';
import { TeacherAttendanceEditStatus } from '@/routes/_layout/teachers/-components/attendance-policy-v2/edit-status.js';
import {
  formatPlainDate,
  FrequentTimeZones,
  isNotNullish,
  OneTimeOnboardingLearningPathNames,
} from '@airreading/common';
import {
  attendanceStatusSelectableValues,
  classEligibilitiesRequiredForLearningPathName,
  STUDENT_GROUP_NOTES_MAX_LENGTH,
  CoachStatus,
  GetCoachesModifier,
  OnboardingLearningPathName,
  StudentGroupWithScheduleAndLevel,
} from '@airreading/common/data';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

export interface CreateEditClassFormProps {
  schoolId: string;
  form: UseFormReturn<z.infer<typeof formCreateEditClassSchema>>;
  from?: StudentGroupWithScheduleAndLevel;
  fromAttendance?: boolean;
}

export const CreateEditClassForm = ({ schoolId, form, from, fromAttendance = false }: CreateEditClassFormProps) => {
  const isOneTimeClass = from?.classTime.type === 'one_time';
  const queryClient = useQueryClient();
  const { watch, setValue, register } = form;
  const attendancePolicyV2Enabled = useFeatureFlag('teacher-attendance-policy-v2', false);
  const attendancePolicyV2AdminEnabled = useFeatureFlag('teacher-attendance-policy-v2-admin', false);
  const showReadOnlyV2Attendance = fromAttendance && attendancePolicyV2AdminEnabled;
  const selectedScheduleId = watch('schedule').value;
  const selectedClassTimeId = watch('classTime').value;
  const overrideStartPlainDate = watch('overrideStartPlainDate');
  const overrideEndPlainDate = watch('overrideEndPlainDate');
  const coachAvailable = watch('coachAvailable');
  const coachAll = watch('coachAll');
  const watchedTeacherType = watch('teacherType');
  useEffect(() => {
    if (!showReadOnlyV2Attendance) {
      return;
    }
    register('attendanceStatus');
  }, [register, showReadOnlyV2Attendance]);

  const needsProtectedPeriodOverride =
    attendancePolicyV2Enabled &&
    !!from &&
    !!(from.final14DayProtection ?? from.attendancePolicyV2?.final14DayProtection)?.isProtected &&
    !fromAttendance &&
    (() => {
      const selectedFromCoach =
        (watchedTeacherType ?? AllUpdateTeacherTypeValues.Available) === AllUpdateTeacherTypeValues.Available
          ? coachAvailable
          : coachAll;
      return !!selectedFromCoach?.value && selectedFromCoach.value !== from.coach?.id;
    })();

  const {
    data: schedules,
    classTimes,
    isPending: isLoadingSchedules,
    classTimeOptions,
  } = useClassTimeOptions({
    schoolId,
  });
  const scheduleOptions: MultiSelectOption[] = useMemo(
    () => schedules?.map(buildNameLikeSelectOptions) || EmptyArray,
    [schedules],
  );
  const selectedSchedule = schedules?.find((s) => s.id === selectedScheduleId);

  const filteredClassTimeOptions = useMemo(() => {
    if (!selectedSchedule) return [];

    return classTimeOptions.filter((ct) =>
      selectedSchedule.classTimes.some(({ id: scheduleClassTimeId }) => scheduleClassTimeId === ct.value),
    );
  }, [classTimeOptions, selectedSchedule]);

  const { learningPaths: allLearningPaths, isPending: isLoadingLearningPaths } = useAllLearningPaths();
  const availableLearningPaths = allLearningPaths.filter(
    // onboarding lessons/learning paths cannot be set from class form. This form
    // is only for recurring classes
    (lp) => !OneTimeOnboardingLearningPathNames.includes(lp.name as OnboardingLearningPathName),
  );
  const learningPathOptions: MultiSelectOption[] = availableLearningPaths.map(buildNameLikeSelectOptions);

  const selectedComboLearningPathId = watch('learningPath').value;
  const selectedLearningPath = availableLearningPaths.find((lp) => lp.id === selectedComboLearningPathId);
  const selectableLessons: MultiSelectOption[] = (selectedLearningPath?.learningPathLessons || []).map(
    ({ lesson }) => ({
      label: lesson.name,
      value: lesson.id,
    }),
  );
  const [selectedTeacherType, setSelectedTeacherType] = useState<string | undefined>(
    AllUpdateTeacherTypeValues.Available,
  );

  useEffect(() => {
    const sub = watch((value, { name }) => {
      if (name === 'schedule' && value.classTime?.value) {
        setValue('classTime', emptySelectOption, { shouldDirty: true, shouldValidate: true });
      }

      if (name === 'learningPath') {
        if (value.startingLesson?.value) {
          setValue('startingLesson', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        }
        setValue('coachAvailable', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        setValue('coachAll', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        queryClient.invalidateQueries(getCoaches());
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, form, queryClient]);

  useEffect(() => {
    // This effect is only for editing existing classes
    if (!from) {
      return;
    }

    // Reset coach dropdowns when teacher type changes
    const sub = watch((value, { name }) => {
      if (name === 'teacherType' && value.teacherType) {
        setValue('coachAvailable', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        setValue('coachAll', emptySelectOption, { shouldDirty: true, shouldValidate: true });

        setSelectedTeacherType(value.teacherType);
        queryClient.invalidateQueries(getCoaches());
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, form, from, queryClient]);

  const learningPathNameForEligibility = selectedLearningPath?.name ?? from?.learningPath?.name ?? '';
  const classEligibilities = classEligibilitiesRequiredForLearningPathName(learningPathNameForEligibility);
  const now = Temporal.Now.plainDateTimeISO(FrequentTimeZones.UsCentral);

  const coachesSearchOptions: GetCoachesModifier =
    selectedSchedule && selectedClassTimeId
      ? {
          pageIndex: 0,
          filters: {
            availabilities: {
              classTimes: [selectedClassTimeId],
              orgId: classTimes[0].schedule.school.org.id,
              classEligibilities,
              overrideStartPlainDate: overrideStartPlainDate ?? Temporal.PlainDate.from(now.toString()),
              overrideEndPlainDate: overrideEndPlainDate ?? Temporal.PlainDate.from(selectedSchedule.endPlainDate),
              shouldAvoidConflicts: isOneTimeClass,
            },
          },
        }
      : {
          pageIndex: 0,
        };

  const coachFilters: CoachesTypeaheadSearchOptions = {
    filters: {
      statuses: [CoachStatus.ready_to_teach],
    },
  };
  const allCoachesSearchOptions: CoachesTypeaheadSearchOptions = {
    shouldDisplayOverallGrade: true,
    ...coachFilters,
  };

  // Add selected coach to available coaches (in case is not available, but still is the original coach)
  const selectedCoachId = from ? from.coach?.id || watch('coach')?.value : null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: originalCoach } = selectedCoachId ? useQuery(getCoach(selectedCoachId)) : { data: null };

  const { data, isLoading: isLoadingCoaches } = useAvailableCoachesSearch(
    coachesSearchOptions,
    { shouldDisplayOverallGrade: true, sortByAvailabilityPriority: true },
    originalCoach,
  );

  const availableCoaches = useMemo(() => data || [], [data]);

  const teacherSelect =
    selectedTeacherType === AllUpdateTeacherTypeValues.Available ? (
      <FormComboBox
        form={form}
        label={'Available teachers'}
        name={'coachAvailable'}
        placeholder="Select teacher"
        {...comboBoxOptionsOf(availableCoaches)}
        shouldHideCheck={true}
        renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
        emptySearchOptionMessage="No available teachers"
        isLoadingKnownOptions={isLoadingCoaches}
      />
    ) : (
      <FormComboBox
        form={form}
        label={'All teachers'}
        name={'coachAll'}
        placeholder="Select teacher"
        {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient, allCoachesSearchOptions))}
        shouldHideCheck={true}
        renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
      />
    );

  return (
    <div className="grid grid-cols-2 gap-2 py-4">
      {!fromAttendance ? (
        <>
          {!from && (
            <>
              <FormComboBox
                form={form}
                label={'Add teacher'}
                name="coach"
                placeholder="Select teacher"
                {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient))}
              />
              <FormMultiSelect
                form={form}
                label={'Add students'}
                name="students"
                multipleSelectorProps={{
                  placeholder: 'Search students by name to add to the group',
                  onSearch: userTypeaheadSearch(queryClient, { schools: [schoolId].filter(isNotNullish) }),
                }}
              />
            </>
          )}
          {!isOneTimeClass && (
            <div className="col-span-2">
              <FormComboBox
                form={form}
                label="Schedule"
                name="schedule"
                placeholder="Select schedule"
                {...comboBoxOptionsOf(scheduleOptions)}
                triggerProps={{
                  disabled: isLoadingSchedules,
                }}
                formDescription={
                  selectedSchedule ? (
                    <div>
                      Dates: {formatPlainDate(selectedSchedule.startPlainDate)} -{' '}
                      {formatPlainDate(selectedSchedule.endPlainDate)}
                    </div>
                  ) : null
                }
              />
            </div>
          )}
          <div className="col-span-2">
            <FormComboBox
              form={form}
              label="Class time (CT)"
              name="classTime"
              placeholder="Select class times"
              {...comboBoxOptionsOf(filteredClassTimeOptions)}
              triggerProps={{
                disabled: isLoadingSchedules || isOneTimeClass,
              }}
            />
          </div>
          {from && (
            <>
              <div className="col-span-2">
                <FormRadioGroup label="Teacher" form={form} name="teacherType" options={AllSelectTeacherTypes} />
              </div>
              <div className="col-span-2">{teacherSelect}</div>
              <div className="col-span-2">
                <FormMultiSelect
                  form={form}
                  label={'Students'}
                  name="students"
                  multipleSelectorProps={{
                    placeholder: 'Search students by name to add to the group',
                    onSearch: userTypeaheadSearch(queryClient, { schools: [schoolId].filter(isNotNullish) }),
                  }}
                />
              </div>
            </>
          )}
          <FormInputDate
            name="overrideStartPlainDate"
            label="Manually override start date"
            form={form}
            showClearDateButton={true}
            description={
              selectedSchedule
                ? `Selected schedule [${selectedSchedule.name}] default start date: ${formatPlainDate(selectedSchedule.startPlainDate)}`
                : null
            }
          />
          <FormInputDate
            name="overrideEndPlainDate"
            label="Manually override end date"
            form={form}
            showClearDateButton={true}
            description={
              selectedSchedule
                ? `Selected schedule [${selectedSchedule.name}] default end date: ${formatPlainDate(selectedSchedule.endPlainDate)}`
                : null
            }
          />
          <FormComboBox
            form={form}
            label="Learning path"
            name="learningPath"
            placeholder="Select learning path"
            {...comboBoxOptionsOf(learningPathOptions)}
            triggerProps={{ disabled: isLoadingLearningPaths }}
          />
          <FormComboBox
            form={form}
            label="Starting lesson"
            name="startingLesson"
            placeholder="Select starting lesson"
            {...comboBoxOptionsOf(selectableLessons)}
            triggerProps={{
              disabled: isLoadingLearningPaths,
            }}
          />
          <div className="col-span-2">
            <FormTextArea
              form={form}
              label="Notes"
              name="notes"
              textAreaProps={{
                className: 'resize-none',
                placeholder: `Add a note (max ${STUDENT_GROUP_NOTES_MAX_LENGTH} characters)`,
              }}
            />
          </div>
        </>
      ) : (
        <>
          <div className="space-y-2 py-4">
            <div className="flex gap-2 items-center">
              <Label className="font-bold">Teacher name</Label>
            </div>
            <p className="text-[#64748B]">{from?.coach ? from.coach.fullNameOrEmail : 'No teacher assigned'}</p>
          </div>
          {showReadOnlyV2Attendance ? (
            <TeacherAttendanceEditStatus
              policy={from?.attendancePolicyV2}
              legacyStatusLabel={from?.attendanceStatus ? getAttendanceStatusLabel(from.attendanceStatus) : null}
            />
          ) : (
            <div className="col-span-2">
              <FormSingleSelect
                form={form}
                label="Attendance status"
                name="attendanceStatus"
                options={attendanceStatusSelectableValues}
                renderOption={getAttendanceStatusLabel}
                triggerProps={{ className: 'w-2/3' }}
              />
            </div>
          )}
          <div className="col-span-2">
            <FormMultiSelect
              form={form}
              name="emailsSent"
              label="Emails sent"
              multipleSelectorProps={{
                options: emailsSentOptions,
                className: 'w-2/3',
              }}
            />
          </div>
          {from?.coachLastUnassignedAtPlainDate && (
            <div className="col-span-2">
              <FormInputDate
                name="coachUnassignedAtPlainDate"
                label="Reassignment date"
                form={form}
                showClearDateButton={true}
              />
            </div>
          )}
          <div className="col-span-2">
            <FormTextArea
              form={form}
              label="Teacher attendance notes"
              name="attendanceNotes"
              textAreaProps={{ className: 'w-2/3 resize-none' }}
            />
          </div>
        </>
      )}

      {needsProtectedPeriodOverride && (
        <div className="col-span-2 space-y-3">
          <Final14DayProtectionNotice
            attendancePolicyV2={from?.attendancePolicyV2}
            final14DayProtection={from?.final14DayProtection}
          />
          <ProtectedPeriodOverrideFields form={form} required />
        </div>
      )}
    </div>
  );
};
